import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  ListItem,
  ListItemText,
} from '@mui/material';
import {Check, ErrorOutline} from '@mui/icons-material';
import React, {useMemo} from 'react';
import {themeColors} from '../../theme/Theme';
import {useHandoverNoteStyles} from '../notes/handoverNoteStyles';
import {useAutoField} from '../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../shared/UPDATES';
import {RiskType} from '../../schema-types/globalTypes';
import {riskNotesFormSectionType} from './riskNotesFields';
import {riskTypeToDisplays} from './riskNotesText';

export const RiskNotesMenuItem: React.FC<{
  menuItemRiskType: RiskType;
  currentRiskType: RiskType;
  setCurrentRiskType: React.Dispatch<React.SetStateAction<RiskType>>;
  readOnly: boolean;
  onToggleNoChange: (riskType: RiskType, section: riskNotesFormSectionType) => void;
}> = ({
  menuItemRiskType,
  currentRiskType,
  setCurrentRiskType,
  readOnly,
  onToggleNoChange,
}) => {
  const classes = useHandoverNoteStyles();
  const [field, , helpers] = useAutoField<riskNotesFormSectionType>(menuItemRiskType);
  const active = useMemo(
    () => currentRiskType === menuItemRiskType,
    [currentRiskType, menuItemRiskType]
  );

  const noChange = field.value?.noChange === true;
  const notes = field.value?.notes || '';
  const isDone = noChange || notes.length > 0;
  const isError = !isDone;
  const {title} = riskTypeToDisplays[menuItemRiskType];

  return (
    <ListItem
      data-test={menuItemRiskType}
      sx={{
        ...classes.rowItem,
        ...(isError ? {backgroundColor: themeColors.lightAlert} : {}),
        ...(isDone ? {backgroundColor: themeColors.lightGreen} : {}),
        ...(active ? classes.activeItem : {}),
      }}
      onClick={() => {
        setCurrentRiskType(menuItemRiskType);
      }}
    >
      <Box
        sx={{
          width: 30,
          pr: 0.5,
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          alignSelf: 'center',
        }}
      >
        {isDone ? <Check data-test="Status Done" htmlColor={themeColors.green} /> : null}
        {isError ? <ErrorOutline color="error" data-test="Status Error" /> : null}
      </Box>
      <Box display="flex" justifyContent="space-between" flex={1}>
        <ListItemText
          primaryTypographyProps={{
            style: {
              paddingBottom: 0,
              fontSize: 16,
              userSelect: 'none',
              display: 'flex',
            },
          }}
          sx={classes.itemText}
        >
          {title}
        </ListItemText>

        <FormControl>
          <FormControlLabel
            disabled={readOnly}
            onClick={(e) => {
              if (readOnly) {
                return;
              }
              e.preventDefault();
              e.stopPropagation();
              const updatedSectionValues: riskNotesFormSectionType = {
                ...field.value,
                noChange: !noChange,
              };
              helpers.setValue(UPDATES._1003, updatedSectionValues);
              onToggleNoChange(menuItemRiskType, updatedSectionValues);
            }}
            control={
              <Checkbox
                data-test="No changes"
                size="small"
                disableRipple={readOnly}
                sx={{
                  '& svg': {
                    color: active ? themeColors.white : themeColors.backgroundDark,
                  },
                  lineHeight: 1,
                }}
                checked={noChange}
              />
            }
            label="No changes"
          />
        </FormControl>
      </Box>
    </ListItem>
  );
};
